import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  AuditEventListItem,
  AuditListParams,
  getAuditEvents,
} from '../../services/auditLogService'
import { AuditActionBadge } from './components/AuditActionBadge'
import { AuditEventDrawer } from './components/AuditEventDrawer'
import { AuditExportModal } from './components/AuditExportModal'

const SECTIONS = [
  { value: 'auth', label: 'Auth' },
  { value: 'members', label: 'Members' },
  { value: 'memberships', label: 'Memberships' },
  { value: 'experiences', label: 'Experiences' },
  { value: 'bookings', label: 'Bookings' },
  { value: 'connections', label: 'Connections' },
  { value: 'forums', label: 'Forums' },
  { value: 'mastermind_circles', label: 'Mastermind Circles' },
  { value: 'expertise', label: 'Expertise' },
  { value: 'payments', label: 'Payments' },
  { value: 'payouts', label: 'Payouts' },
]

const ACTIONS = [
  { value: 'login', label: 'Login' },
  { value: 'created', label: 'Created' },
  { value: 'updated', label: 'Updated' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'purchased', label: 'Purchased' },
]

const USER_TYPES = [
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'STAFF', label: 'Staff' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'FINAL_USER', label: 'Member' },
  { value: 'TEAM', label: 'Team' },
]

const PAGE_SIZE = 50

const isoStartOfDay = (d: Date) => {
  const c = new Date(d)
  c.setHours(0, 0, 0, 0)
  return c.toISOString()
}
const isoEndOfDay = (d: Date) => {
  const c = new Date(d)
  c.setHours(23, 59, 59, 999)
  return c.toISOString()
}

const defaultDateFrom = () => {
  const d = new Date()
  d.setDate(d.getDate() - 30)
  return isoStartOfDay(d)
}

const initials = (actor: AuditEventListItem['actor']) => {
  const name = actor.name || 'System'
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || '·'
}

const formatTimestamp = (iso: string) => {
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    })
  } catch {
    return iso
  }
}

const sectionLabel = (v: string) => SECTIONS.find((s) => s.value === v)?.label || v

interface FilterState {
  actor: string
  userType: string[]
  section: string[]
  action: string[]
  dateFromDate: string      // YYYY-MM-DD (input value)
  dateToDate: string        // YYYY-MM-DD
  q: string
  includeArchive: boolean
}

const emptyFilters = (): FilterState => {
  const now = new Date()
  const past = new Date()
  past.setDate(past.getDate() - 30)
  return {
    actor: '',
    userType: [],
    section: [],
    action: [],
    dateFromDate: past.toISOString().slice(0, 10),
    dateToDate: now.toISOString().slice(0, 10),
    q: '',
    includeArchive: false,
  }
}

export const AuditLogPage: FC = () => {
  const [rows, setRows] = useState<AuditEventListItem[]>([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<FilterState>(() => emptyFilters())
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [showExport, setShowExport] = useState(false)

  const buildParams = useCallback(
    (targetPage: number): AuditListParams => ({
      page: targetPage,
      pageSize: PAGE_SIZE,
      actor: filters.actor.trim() || undefined,
      userType: filters.userType.join(',') || undefined,
      section: filters.section.join(',') || undefined,
      action: filters.action.join(',') || undefined,
      dateFrom: filters.dateFromDate ? isoStartOfDay(new Date(filters.dateFromDate)) : undefined,
      dateTo: filters.dateToDate ? isoEndOfDay(new Date(filters.dateToDate)) : undefined,
      q: filters.q.trim() || undefined,
      includeArchive: filters.includeArchive || undefined,
    }),
    [filters]
  )

  const load = useCallback(
    async (targetPage = 1) => {
      setLoading(true)
      setError(null)
      try {
        const data = await getAuditEvents(buildParams(targetPage))
        setRows(data.results)
        setCount(data.count)
        setPage(targetPage)
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || 'Could not load audit events.')
        setRows([])
        setCount(0)
      } finally {
        setLoading(false)
      }
    },
    [buildParams]
  )

  useEffect(() => {
    load(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.actor,
    filters.userType.join(','),
    filters.section.join(','),
    filters.action.join(','),
    filters.dateFromDate,
    filters.dateToDate,
    filters.q,
    filters.includeArchive,
  ])

  const totalPages = useMemo(() => Math.max(1, Math.ceil(count / PAGE_SIZE)), [count])

  const toggleMulti = (key: 'userType' | 'section' | 'action') => (value: string) => {
    setFilters((f) => {
      const current = f[key]
      return {
        ...f,
        [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
      }
    })
  }

  const clearFilters = () => setFilters(emptyFilters())

  const openExport = () => setShowExport(true)

  return (
    <>
      <div className="nb-audit">
        <header className="nb-audit__header">
          <div>
            <h1 className="nb-audit__title">Audit Log</h1>
            <div className="nb-audit__meta">
              Compliance trail · immutable · read-only
            </div>
          </div>
          <div className="nb-audit__actions">
            <button className="nb-audit__btn nb-audit__btn--outline" onClick={() => load(page)} disabled={loading}>
              Refresh
            </button>
            <button className="nb-audit__btn nb-audit__btn--primary" onClick={openExport} disabled={loading || count === 0}>
              Export
            </button>
          </div>
        </header>

        <div className="nb-audit__filter-bar">
          <div className="nb-audit__field">
            <span className="nb-audit__field-label">User type</span>
            <MultiChips
              options={USER_TYPES}
              selected={filters.userType}
              onToggle={toggleMulti('userType')}
            />
          </div>
          <div className="nb-audit__field">
            <span className="nb-audit__field-label">Section</span>
            <MultiChips
              options={SECTIONS}
              selected={filters.section}
              onToggle={toggleMulti('section')}
            />
          </div>
          <div className="nb-audit__field">
            <span className="nb-audit__field-label">Action</span>
            <MultiChips
              options={ACTIONS}
              selected={filters.action}
              onToggle={toggleMulti('action')}
            />
          </div>
          <div className="nb-audit__field">
            <span className="nb-audit__field-label">Date range</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="date"
                className="nb-audit__field-input nb-audit__field-input--date"
                value={filters.dateFromDate}
                onChange={(e) => setFilters((f) => ({ ...f, dateFromDate: e.target.value }))}
              />
              <span style={{ color: '#808080', fontSize: 12 }}>→</span>
              <input
                type="date"
                className="nb-audit__field-input nb-audit__field-input--date"
                value={filters.dateToDate}
                onChange={(e) => setFilters((f) => ({ ...f, dateToDate: e.target.value }))}
              />
            </div>
          </div>
          <div className="nb-audit__filter-actions">
            <label className="nb-audit__archive-toggle">
              <input
                type="checkbox"
                checked={filters.includeArchive}
                onChange={(e) => setFilters((f) => ({ ...f, includeArchive: e.target.checked }))}
              />
              Include archive
            </label>
            <button className="nb-audit__clear" onClick={clearFilters}>Clear filters</button>
          </div>
        </div>

        {filters.includeArchive && (
          <div className="nb-audit__archive-notice">
            <span>◈</span>
            Queries including the archive can take a few seconds.
          </div>
        )}

        <div className="nb-audit__table-wrap">
          <div className="nb-audit__status-row">
            <span>{loading ? 'Loading…' : `Showing ${rows.length.toLocaleString()} of ${count.toLocaleString()} events`}</span>
            <span>Page {page} of {totalPages}</span>
          </div>

          {error && <div className="nb-audit__empty">{error}</div>}
          {!error && rows.length === 0 && !loading && (
            <div className="nb-audit__empty">No events match the current filters.</div>
          )}
          {!error && rows.length > 0 && (
            <table className="nb-audit__table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Actor</th>
                  <th>Section</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} onClick={() => setSelectedId(row.id)}>
                    <td className="nb-audit__cell-timestamp">{formatTimestamp(row.createdAt)}</td>
                    <td>
                      <div className="nb-audit__cell-actor">
                        <span
                          className="nb-audit__avatar"
                          style={{ background: row.actor.avatarUrl ? `url(${row.actor.avatarUrl})` : '#C9C9C9' }}
                        >
                          {!row.actor.avatarUrl && initials(row.actor)}
                        </span>
                        <div className="nb-audit__actor-name">
                          <span>{row.actor.name}</span>
                          <span className="nb-audit__actor-role">{row.actor.role || 'system'}</span>
                        </div>
                      </div>
                    </td>
                    <td><span className="nb-audit__section-label">{sectionLabel(row.section)}</span></td>
                    <td><AuditActionBadge action={row.action} /></td>
                    <td>
                      <span className="nb-audit__target-label">{row.target.label || row.target.entityType || '—'}</span>
                      {row.target.entityId != null && (
                        <span className="nb-audit__target-id">#{row.target.entityId}</span>
                      )}
                    </td>
                    <td><span className="nb-audit__summary" title={row.summary || ''}>{row.summary || '—'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="nb-audit__pagination">
            <button onClick={() => load(page - 1)} disabled={loading || page <= 1}>← Prev</button>
            <button onClick={() => load(page + 1)} disabled={loading || page >= totalPages}>Next →</button>
          </div>
        </div>
      </div>

      {selectedId != null && (
        <AuditEventDrawer eventId={selectedId} onClose={() => setSelectedId(null)} />
      )}
      {showExport && (
        <AuditExportModal
          filters={buildParams(1)}
          rowCount={count}
          onClose={() => setShowExport(false)}
        />
      )}
    </>
  )
}

// ---------- inline component: multi-chip selector ----------
interface MultiChipsProps {
  options: { value: string; label: string }[]
  selected: string[]
  onToggle: (v: string) => void
}
const MultiChips: FC<MultiChipsProps> = ({ options, selected, onToggle }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
    {options.map((o) => {
      const active = selected.includes(o.value)
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onToggle(o.value)}
          style={{
            padding: '4px 10px',
            fontSize: 11,
            fontFamily: "'Satoshi', sans-serif",
            border: '1px solid #E9E9E9',
            background: active ? '#151515' : '#fff',
            color: active ? '#fff' : '#151515',
            borderColor: active ? '#151515' : '#E9E9E9',
            cursor: 'pointer',
            borderRadius: 3,
            letterSpacing: '0.05em',
          }}
        >
          {o.label}
        </button>
      )
    })}
  </div>
)
